import { House, LayoutGrid, Search, ShoppingBag, User, type LucideIcon } from 'lucide-react';
import type { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router';

import { useCustomerAuth } from '@/features/storefront/account/hooks/useCustomerAuth';
import { useQuickCart } from '@/features/storefront/components/QuickCart';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { useCart } from '@/features/storefront/hooks/useCart';
import { resolveVariant } from '@/features/storefront/layouts/families';
import { useThemeLayout } from '@/features/storefront/layouts/useThemeLayout';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { pluginEnabled, themePlugin } from '@/features/storefront/plugins/registry';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';
import { useStorefrontTheme } from '@/features/storefront/useStorefrontTheme';
import { cn } from '@/lib/cn';

/**
 * Whether the app bar plugin is on for the shop being worn.
 *
 * The shell reads it to set `data-app-bar`, which is what makes room for the
 * bar on a phone (see `index.css`); the product page reads it to trade its
 * breadcrumb for a back link there.
 */
export function useAppTabBar(): boolean {
  const theme = useStorefrontTheme();
  const family = resolveVariant(useThemeLayout(), 'header');
  return pluginEnabled(theme, 'app_tab_bar', family);
}

const BAR_CLASS = 'inset-x-0 bottom-0 z-40 border-t';

const BAR_STYLE = {
  borderColor: 'var(--storefront-surface)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-muted)',
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
} as const;

/**
 * The app bar plugin: on a phone, the shop's way around in a bar fixed to the
 * bottom of the screen - home, categories, search, cart, account - the way an
 * installed app has it.
 *
 * Below `md` only. From there up the header has room for every one of these,
 * and a second copy of them pinned under the page would be clutter. Each tab
 * is drawn only where the shop has it, by the same features the header's
 * corner reads, so a shop with no cart has no cart tab rather than a tab
 * that 404s.
 */
export function AppTabBar() {
  const on = useAppTabBar();
  const theme = useStorefrontTheme();
  if (!on) return null;
  return <LiveBar showLabels={themePlugin(theme, 'app_tab_bar').show_labels} />;
}

function LiveBar({ showLabels }: { showLabels: boolean }) {
  const paths = useStorefrontPaths();
  const features = useStorefrontFeatures();
  const text = useShopContent();
  const { pathname } = useLocation();
  const { status } = useCustomerAuth();

  const home = paths.home();
  const categories = paths.categories();
  const search = paths.search();
  const account = status === 'authenticated' ? paths.account() : paths.accountLogin();
  // Home is every address's prefix, so only an exact match lights it; the
  // others light on the page itself and anything filed under it.
  const at = (to: string) => pathname === to || pathname === `${to}/`;
  const under = (to: string) => at(to) || pathname.startsWith(`${to}/`);

  return (
    <nav
      aria-label={text('header.menu')}
      data-testid="app-tab-bar"
      className={cn('fixed flex md:hidden', BAR_CLASS)}
      style={BAR_STYLE}
    >
      <Tab to={home} icon={House} label={text('app_bar.home')} active={at(home)} showLabel={showLabels} />
      <Tab
        to={categories}
        icon={LayoutGrid}
        label={text('app_bar.categories')}
        active={under(categories) || under(paths.products())}
        showLabel={showLabels}
      />
      {features.search ? (
        <Tab to={search} icon={Search} label={text('app_bar.search')} active={under(search)} showLabel={showLabels} />
      ) : null}
      {/* Not rendered rather than hidden, for the reason `ShopperActions`
          gives: reading the basket creates one. */}
      {features.cart ? <CartTab active={under(paths.cart())} showLabel={showLabels} /> : null}
      {features.accounts ? (
        <Tab
          to={account}
          icon={User}
          label={text('app_bar.account')}
          active={under(paths.account())}
          showLabel={showLabels}
        />
      ) : null}
    </nav>
  );
}

/**
 * The cart tab, with its live item count. Like the header's cart link, a
 * plain tap opens the quick cart's drawer where the theme has one.
 */
function CartTab({ active, showLabel }: { active: boolean; showLabel: boolean }) {
  const paths = useStorefrontPaths();
  const text = useShopContent();
  const cart = useCart();
  const quickCart = useQuickCart();
  const count = (cart.data?.items ?? []).reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Tab
      to={paths.cart()}
      icon={ShoppingBag}
      label={text('app_bar.cart')}
      active={active}
      showLabel={showLabel}
      badge={count}
      onClick={(event) => {
        if (!quickCart.enabled) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        quickCart.openCart();
      }}
    />
  );
}

interface TabProps {
  to: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
  showLabel: boolean;
  badge?: number;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

function Tab({ to, icon, label, active, showLabel, badge, onClick }: TabProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={showLabel ? undefined : label}
      className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-2"
      style={active ? { color: 'var(--storefront-primary)' } : undefined}
    >
      <TabFace icon={icon} label={label} active={active} showLabel={showLabel} badge={badge} />
    </Link>
  );
}

function TabFace({
  icon: Icon,
  label,
  active,
  showLabel,
  badge,
}: Pick<TabProps, 'icon' | 'label' | 'active' | 'showLabel' | 'badge'>) {
  return (
    <>
      <span className="relative">
        <Icon className="size-[1.375rem]" strokeWidth={active ? 2.25 : 1.75} aria-hidden />
        {badge ? (
          <span
            className="absolute -end-2.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[0.625rem] font-semibold leading-none"
            style={{ backgroundColor: 'var(--storefront-primary)', color: 'var(--storefront-background)' }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </span>
      {showLabel ? (
        <span className={cn('max-w-full truncate text-[0.6875rem] leading-tight', active && 'font-semibold')}>
          {label}
        </span>
      ) : null}
    </>
  );
}

/**
 * The customiser's stand-in: the same bar, inert, at the foot of the canvas
 * and only in its phone frame - the one size a shopper would see it at. No
 * router, basket or session out there, so no links, count or sign-in state.
 */
export function AppTabBarPreview({ theme, family }: { theme: StorefrontTheme; family: string }) {
  const features = useStorefrontFeatures();
  const text = useShopContent();
  if (!pluginEnabled(theme, 'app_tab_bar', family)) return null;
  const showLabel = themePlugin(theme, 'app_tab_bar').show_labels;
  const tabs: [LucideIcon, string, boolean][] = [
    [House, text('app_bar.home'), true],
    [LayoutGrid, text('app_bar.categories'), true],
    [Search, text('app_bar.search'), features.search],
    [ShoppingBag, text('app_bar.cart'), features.cart],
    [User, text('app_bar.account'), features.accounts],
  ];
  return (
    <div aria-hidden className={cn('sticky hidden preview-phone:flex!', BAR_CLASS)} style={BAR_STYLE}>
      {tabs
        .filter(([, , shown]) => shown)
        .map(([icon, label], index) => (
          <span
            key={label}
            className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-2"
            style={index === 0 ? { color: 'var(--storefront-primary)' } : undefined}
          >
            <TabFace icon={icon} label={label} active={index === 0} showLabel={showLabel} />
          </span>
        ))}
    </div>
  );
}
